<template>
  <div class="agent-call-usage-overview" v-loading="loading">
    <div class="usage-header">
      <el-alert
        class="service-hint"
        title="单次调用消耗：主脑对话、意图识别、看图、出图、联网搜索、读页等。搜索/读页按次计费，Token 列为 — 属正常；列表灰色小字是内部编码。"
        type="info"
        :closable="false"
        show-icon
      />

      <el-row :gutter="16" class="stats-row">
        <el-col :xs="12" :sm="6">
          <div class="stat-card">
            <div class="stat-value">{{ summary.count }}</div>
            <div class="stat-label">记录数</div>
          </div>
        </el-col>
        <el-col :xs="12" :sm="6">
          <div class="stat-card">
            <div class="stat-value">{{ formatNumber(summary.totalTokens) }}</div>
            <div class="stat-label">总 Token</div>
          </div>
        </el-col>
        <el-col :xs="12" :sm="6">
          <div class="stat-card is-cost">
            <div class="stat-value">{{ formatMoney(summary.costCents) }}</div>
            <div class="stat-label">厂商成本合计</div>
          </div>
        </el-col>
        <el-col :xs="12" :sm="6">
          <div class="stat-card">
            <div class="stat-value">{{ formatNumber(summary.quantity) }}</div>
            <div class="stat-label">计费数量合计</div>
          </div>
        </el-col>
      </el-row>

      <div class="toolbar">
        <el-input
          v-model="filters.keyword"
          placeholder="任务名 / 模型 / usageId / runId"
          clearable
          class="toolbar-item"
          @keyup.enter="handleSearch"
        />
        <el-input
          v-model="filters.provider"
          placeholder="厂商"
          clearable
          class="toolbar-item toolbar-item--sm"
          @keyup.enter="handleSearch"
        />
        <el-select v-model="filters.modality" placeholder="模态" clearable class="toolbar-item toolbar-item--sm">
          <el-option label="对话" value="chat" />
          <el-option label="图像" value="image" />
          <el-option label="视觉理解" value="vision" />
          <el-option label="联网搜索" value="search" />
          <el-option label="网页抓取" value="fetch" />
          <el-option label="视频" value="video" />
          <el-option label="3D" value="3d" />
        </el-select>
        <el-select v-model="filters.billingMode" placeholder="计费方式" clearable class="toolbar-item toolbar-item--sm">
          <el-option label="按 Token" value="token" />
          <el-option label="按张" value="image" />
          <el-option label="按秒" value="second" />
          <el-option label="按次" value="request" />
          <el-option label="积分" value="credit" />
        </el-select>
        <el-select v-model="filters.taskKind" placeholder="任务类型" clearable class="toolbar-item toolbar-item--sm">
          <el-option label="主脑对话" value="brain" />
          <el-option label="路由/意图" value="router" />
          <el-option label="看图理解" value="vl" />
          <el-option label="出图/修图" value="image" />
          <el-option label="LLM" value="llm" />
          <el-option label="问卷" value="survey" />
          <el-option label="研究" value="research" />
          <el-option label="联网搜索" value="search" />
          <el-option label="网页抓取" value="fetch" />
        </el-select>
        <el-button type="primary" @click="handleSearch">查询</el-button>
        <el-button @click="handleReset">重置</el-button>
        <el-button :loading="loading" @click="loadData">刷新</el-button>
      </div>
    </div>

    <div class="table-scroll">
    <el-table :data="items" border stripe empty-text="暂无调用消耗数据" class="usage-table" height="100%">
      <el-table-column prop="createTime" label="时间" width="170" fixed="left" />
      <el-table-column label="完成任务" min-width="220">
        <template #default="{ row }">
          <div class="task-cell">
            <strong>{{ taskTitle(row) }}</strong>
            <span class="muted" :title="taskCodeHint(row)">{{ taskCodeHint(row) }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="厂商 / 模型" min-width="240">
        <template #default="{ row }">
          <div class="task-cell">
            <div class="model-title">
              <strong>{{ providerLabel(row.provider) }}</strong>
              <el-tag
                v-if="seedreamVariantTag(row)"
                size="small"
                :type="seedreamVariantTag(row) === 'Pro' ? 'warning' : 'info'"
                class="variant-tag"
              >
                {{ seedreamVariantTag(row) }}
              </el-tag>
            </div>
            <span class="muted">{{ modelDisplayName(row) }}</span>
            <span class="muted mono" :title="row.model || ''">{{ row.model || '—' }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="模态" width="100">
        <template #default="{ row }">{{ modalityLabel(row.modality) }}</template>
      </el-table-column>
      <el-table-column label="计费" width="100">
        <template #default="{ row }">{{ billingLabel(row.billingMode) }}</template>
      </el-table-column>
      <el-table-column label="Token" width="140">
        <template #default="{ row }">
          <span v-if="Number(row.totalTokens) > 0">
            {{ row.promptTokens || 0 }}+{{ row.completionTokens || 0 }}={{ row.totalTokens || 0 }}
          </span>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column label="数量" width="110">
        <template #default="{ row }">
          <span v-if="hasQuantity(row)">{{ row.quantity }} {{ row.quantityUnit || '' }}</span>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column label="成本" width="120">
        <template #default="{ row }">
          <div class="task-cell">
            <strong v-if="resolveRowCostCents(row) > 0">{{ formatMoney(resolveRowCostCents(row), row.currency) }}</strong>
            <span v-else class="muted">—</span>
            <span v-if="Number(row.unitPriceCents) > 0" class="muted">
              单价 {{ formatMoney(row.unitPriceCents, row.currency) }}/{{ row.quantityUnit || '单位' }}
            </span>
            <span v-else-if="tokenPricingHint(row)" class="muted">
              {{ tokenPricingHint(row) }}
            </span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="callStatus" label="状态" width="90">
        <template #default="{ row }">
          <el-tag size="small" :type="statusType(row.callStatus)">{{ statusLabel(row.callStatus) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="userId" label="用户" width="120" show-overflow-tooltip />
      <el-table-column label="操作" width="90" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" link @click="openDetail(row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>
    </div>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next, sizes"
        :total="total"
        :current-page="page"
        :page-size="pageSize"
        :page-sizes="[20, 50, 100]"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog v-model="detailVisible" title="调用消耗详情" width="760px" destroy-on-close>
      <el-descriptions v-if="activeItem" :column="1" border>
        <el-descriptions-item label="任务">{{ taskTitle(activeItem) }}</el-descriptions-item>
        <el-descriptions-item label="内部编码">{{ taskCodeHint(activeItem) || '—' }}</el-descriptions-item>
        <el-descriptions-item label="Skill / Result">{{ skillLabel(activeItem.skillId) }} / {{ activeItem.resultId || '—' }}</el-descriptions-item>
        <el-descriptions-item label="厂商 / 模型">
          {{ providerLabel(activeItem.provider) }} /
          {{ modelDisplayName(activeItem) }}
          <el-tag
            v-if="seedreamVariantTag(activeItem)"
            size="small"
            :type="seedreamVariantTag(activeItem) === 'Pro' ? 'warning' : 'info'"
            class="variant-tag"
          >
            {{ seedreamVariantTag(activeItem) }}
          </el-tag>
          <span class="muted mono">（{{ activeItem.model || '—' }}）</span>
        </el-descriptions-item>
        <el-descriptions-item label="模态 / 计费">{{ modalityLabel(activeItem.modality) }} / {{ billingLabel(activeItem.billingMode) }}</el-descriptions-item>
        <el-descriptions-item label="Token">
          prompt {{ activeItem.promptTokens || 0 }} · completion {{ activeItem.completionTokens || 0 }} · total {{ activeItem.totalTokens || 0 }}
        </el-descriptions-item>
        <el-descriptions-item label="数量">{{ hasQuantity(activeItem) ? `${activeItem.quantity} ${activeItem.quantityUnit || ''}` : '—' }}</el-descriptions-item>
        <el-descriptions-item label="单价 / 成本">
          {{ activeItem.unitPriceCents != null ? formatMoney(activeItem.unitPriceCents, activeItem.currency) : '—' }}
          /
          {{ formatMoney(resolveRowCostCents(activeItem), activeItem.currency) }}
          <span v-if="pricingHint(activeItem) || tokenPricingHint(activeItem)" class="muted">
            （{{ pricingHint(activeItem) || tokenPricingHint(activeItem) }}）
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="耗时">{{ activeItem.latencyMs != null ? `${activeItem.latencyMs} ms` : '—' }}</el-descriptions-item>
        <el-descriptions-item label="Trace / Run / Request">
          {{ activeItem.traceId || '—' }} / {{ activeItem.runId || '—' }} / {{ activeItem.requestId || '—' }}
        </el-descriptions-item>
        <el-descriptions-item label="项目 / 会话">{{ activeItem.projectId || '—' }} / {{ activeItem.sessionId || '—' }}</el-descriptions-item>
        <el-descriptions-item label="错误">{{ activeItem.errorCode || '—' }} {{ activeItem.errorMessage || '' }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ activeItem.memo || '—' }}</el-descriptions-item>
        <el-descriptions-item label="扩展 JSON">
          <pre class="detail-json">{{ formatExtra(activeItem.extraJson) }}</pre>
        </el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { fetchAgentCallUsageAdminList } from '@/api/agentCallUsage'
import { formatModelDisplayName, seedreamVariantTag as resolveSeedreamVariantTag } from '@/utils/modelDisplay'
import { resolveRowCostCents, tokenPricingHint } from '@/utils/tokenPricing'

const loading = ref(false)
const items = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)
const summary = reactive({
  count: 0,
  totalTokens: 0,
  costCents: 0,
  quantity: 0
})
const filters = reactive({
  keyword: '',
  provider: '',
  modality: '',
  billingMode: '',
  taskKind: ''
})
const detailVisible = ref(false)
const activeItem = ref(null)

const TASK_TITLE_MAP = {
  'agent.chat': '主脑对话',
  'harness.dispatch': '路由分发',
  'harness.conversation_intent': '对话续作判断',
  'visual.intent': '改图语义理解',
  'visual.planner': '多步改图规划',
  'visual.vl': '看图理解',
  'visual.generate': '文生图',
  'visual.edit': '图片编辑',
  'visual.remove_bg': '去背景',
  'visual.image': '图像调用',
  'llm.chat': '模型对话',
  'research.competitor': '竞品研究',
  'research.product_vl': '产品图理解',
  'research.competitor_plan': '竞品研究规划',
  'research.competitor_expand': '竞品扩词重搜',
  'research.web_search': '联网搜索',
  'research.page_fetch': '网页深读',
  'research.competitor_vl': '竞品图理解',
  'research.competitor_synthesize': '竞品表合成',
  'research.competitor_qc': '竞品质检'
}

const SKILL_LABEL_MAP = {
  erase: '消除',
  edit: '编辑',
  'generate-image': '生成图片',
  'remove-bg': '去背景',
  upscale: '高清放大',
  crop: '裁剪',
  'edit-image-text': '改图内文字',
  inpaint: '局部重绘'
}

const PROVIDER_LABEL_MAP = {
  dashscope: '阿里云百炼',
  DASHSCOPE: '阿里云百炼',
  ARK: '火山方舟',
  ark: '火山方舟',
  DEEPSEEK: 'DeepSeek',
  deepseek: 'DeepSeek',
  BOCHA: '博查 Bocha',
  bocha: '博查 Bocha',
  TAVILY: 'Tavily',
  tavily: 'Tavily',
  crawl4ai: 'Crawl4AI',
  light_fetch: '轻量抓取',
  OPENAI: 'OpenAI',
  openai: 'OpenAI'
}

function hasQuantity(row) {
  return row && row.quantity != null && Number(row.quantity) > 0
}

function skillLabel(skillId) {
  const id = String(skillId || '').trim()
  if (!id) return '—'
  return SKILL_LABEL_MAP[id] || id
}

function taskTitle(row) {
  if (!row) return '—'
  const code = String(row.taskName || '').trim()
  const skill = String(row.skillId || '').trim()
  if (code === 'visual.edit' && skill) {
    return `图片编辑 · ${skillLabel(skill)}`
  }
  if (TASK_TITLE_MAP[code]) return TASK_TITLE_MAP[code]
  if (skill && SKILL_LABEL_MAP[skill]) return SKILL_LABEL_MAP[skill]
  if (code) return code
  return skillLabel(skill)
}

function taskCodeHint(row) {
  if (!row) return ''
  const parts = []
  if (row.taskName) parts.push(row.taskName)
  if (row.taskKind) parts.push(row.taskKind)
  if (row.skillId) parts.push(row.skillId)
  return parts.join(' · ')
}

function providerLabel(provider) {
  const key = String(provider || '').trim()
  if (!key) return '—'
  return PROVIDER_LABEL_MAP[key] || key
}

function modalityLabel(modality) {
  const map = {
    chat: '对话',
    image: '图像',
    vision: '视觉理解',
    search: '联网搜索',
    fetch: '网页抓取',
    video: '视频',
    '3d': '3D',
    embedding: '向量'
  }
  const key = String(modality || '').trim()
  return map[key] || key || '—'
}

function billingLabel(mode) {
  const map = {
    token: '按 Token',
    image: '按张',
    second: '按秒',
    request: '按次',
    credit: '积分'
  }
  const key = String(mode || '').trim()
  return map[key] || key || '—'
}

function formatNumber(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return '0'
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 4 })
}

function formatMoney(cents, currency = 'CNY') {
  const n = Number(cents || 0) / 100
  const cur = currency || 'CNY'
  try {
    return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: cur }).format(n)
  } catch {
    return `${n.toFixed(2)} ${cur}`
  }
}

function statusType(status) {
  if (status === 'success') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'partial') return 'warning'
  return 'info'
}

function statusLabel(status) {
  const map = { success: '成功', failed: '失败', partial: '部分' }
  const key = String(status || '').trim()
  return map[key] || key || '—'
}

function parseExtra(raw) {
  if (!raw) return null
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return null
  }
}

function formatExtra(raw) {
  const extra = parseExtra(raw)
  if (!extra) return raw ? String(raw) : '—'
  return JSON.stringify(extra, null, 2)
}

function rowExtra(row) {
  return parseExtra(row?.extraJson)
}

function modelDisplayName(row) {
  if (!row) return '—'
  return formatModelDisplayName(row.model, rowExtra(row))
}

function seedreamVariantTag(row) {
  if (!row) return ''
  return resolveSeedreamVariantTag(row.model, rowExtra(row))
}

function pricingHint(row) {
  const extra = rowExtra(row)
  if (!extra) return ''
  try {
    const pricing = extra?.pricing
    if (!pricing) return ''
    const bits = []
    if (pricing.sizeTier) bits.push(`尺寸 ${pricing.sizeTier}`)
    if (pricing.rule) bits.push(pricing.rule)
    return bits.join(' · ')
  } catch {
    return ''
  }
}

async function loadData() {
  loading.value = true
  try {
    const data = await fetchAgentCallUsageAdminList({
      keyword: filters.keyword || undefined,
      provider: filters.provider || undefined,
      modality: filters.modality || undefined,
      billingMode: filters.billingMode || undefined,
      taskKind: filters.taskKind || undefined,
      page: page.value,
      size: pageSize.value
    })
    items.value = data?.records || []
    total.value = Number(data?.total || 0)
    const s = data?.summary || {}
    summary.count = Number(s.count || 0)
    summary.totalTokens = Number(s.totalTokens || 0)
    summary.costCents = items.value.reduce((sum, row) => sum + resolveRowCostCents(row), 0)
      || Number(s.costCents || 0)
    summary.quantity = Number(s.quantity || 0)
  } catch (error) {
    items.value = []
    total.value = 0
    ElMessage.error(error?.message || '加载调用消耗失败')
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  page.value = 1
  loadData()
}

function handleReset() {
  filters.keyword = ''
  filters.provider = ''
  filters.modality = ''
  filters.billingMode = ''
  filters.taskKind = ''
  page.value = 1
  loadData()
}

function handlePageChange(val) {
  page.value = val
  loadData()
}

function handleSizeChange(val) {
  pageSize.value = val
  page.value = 1
  loadData()
}

function openDetail(row) {
  activeItem.value = row
  detailVisible.value = true
}

onMounted(loadData)
</script>

<style scoped>
.agent-call-usage-overview {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}
.usage-header {
  flex-shrink: 0;
}
.service-hint { margin-bottom: 14px; }
.stats-row { margin-bottom: 14px; }
.stat-card {
  background: #f7f8fa;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.stat-card.is-cost { background: #f0f7ff; }
.stat-value { font-size: 22px; font-weight: 700; line-height: 1.2; }
.stat-label { margin-top: 6px; color: #8a8f98; font-size: 13px; }
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  align-items: center;
}
.toolbar-item { width: 220px; }
.toolbar-item--sm { width: 140px; }
.table-scroll {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.usage-table { width: 100%; }
.usage-table :deep(.cell) { line-height: 1.35; }
.task-cell { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.task-cell strong { word-break: break-word; }
.model-title { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.variant-tag { vertical-align: middle; }
.muted { color: #909399; font-size: 12px; word-break: break-all; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
.pager {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}
.detail-json {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  max-height: 240px;
  overflow: auto;
}
</style>
